import ClientAbstract from './ClientAbstract';
import HttpClient from './HttpClient';
import { mapConsentFeaturesTo } from '../util/consents';
import {
  ApiLoadConsentResponse, ConsentSaveType, ConsentType,
} from '../types/common';
import { FeatureType } from '../defs/features';
import { ConfigState } from '../store/config';
import { AuthState } from '../store/auth';

type Consents4p = {
  id: string;
  status: 'revoked' | 'active';
};

class Client4p extends ClientAbstract {
  private httpClient4p: HttpClient;

  constructor(config: ConfigState, auth: AuthState) {
    super(config, auth);
    const { apiUri4p, accesstoken4p } = config;

    const headers = {
      Accept: 'application/json; charset=utf-8',
      ...(accesstoken4p ? { Authorization: `Bearer ${accesstoken4p}` } : {}),
    };

    this.httpClient4p = new HttpClient(apiUri4p, headers);
  }

  getMappingPrefix() {
    const { spId, lcId } = this.getCustomerInfoFromConfigOrAuth();
    return `cid:${spId}:${lcId}:`;
  }

  async getConsents(lifecycle: string) {
    const { spId, lcId } = this.getCustomerInfoFromConfigOrAuth();
    const mappingPrefix = this.getMappingPrefix();

    const path = lcId
      ? `/consent/sp/${spId}/customer/${lcId}/ui-aspects`
      : `/consent/sp/${spId}/register`;

    const qs = {
      lifecycle,
      textformat: 'html',
      ...(lcId ? { ignoreNotFound: true } : {}),
      additionalInfo: true,
      pageformat: true,
    };

    const uiAspects = await this.httpClient.get<ApiLoadConsentResponse>(path, qs);
    const consents = await this.httpClient4p.get<Consents4p[]>(
      '/consents',
      { purpose_levels: 'consent,legitimate_interest' },
    );

    consents.forEach(({ id: uniqId, status }) => {
      const uniqIdWithoutPrefix = uniqId.substring(mappingPrefix.length);
      const granted = status === 'active';
      const [id, featureType, featureId] = uniqIdWithoutPrefix.split(':') as [
        string,
        FeatureType,
        string,
      ];
      let consent: ConsentType | undefined;
      uiAspects.groups.find((consentGroup) => {
        consent = consentGroup.consents.find((c) => c.id === id);
        return !!consent;
      });

      if (consent) {
        if (!featureType) {
          consent.granted = granted;
        } else {
          const feature = consent[featureType]?.find((f) => f.id === featureId);
          if (feature) {
            feature.granted = granted;
          }
        }
      }
    });

    return uiAspects;
  }

  async saveConsents(data: ConsentSaveType[]) {
    const {
      channel = 'Web', lifecycle, usertype, references,
    } = this.config;
    const mappedConsents: Consents4p[] = [];
    const mappingPrefix = this.getMappingPrefix();

    data.forEach((cons) => {
      const { id, granted } = cons;
      mappedConsents.push({
        id: `${mappingPrefix}${id}`,
        status: granted ? 'active' : 'revoked',
      });

      const featuresMapped = mapConsentFeaturesTo<Consents4p>(
        cons,
        ({ id: fid, granted: fgranted = false }, featureName) => ({
          id: `${mappingPrefix}${id}:${featureName}:${fid}`,
          status: fgranted ? 'active' : 'revoked',
        }),
      );

      if (Object.keys(featuresMapped).length > 0) {
        mappedConsents.push(
          ...(featuresMapped[Object.keys(featuresMapped)[0] as FeatureType]
            || []),
        );
      }
    });

    await this.httpClient4p.post(
      '/consents/update-list',
      {
        consents: mappedConsents,
        triggered_by: JSON.stringify({ // WORKAROUND: WPCSCM-533
          lifecycle, // lifecycle is required, the rest is optional...
          ...(usertype ? { usertype } : {}),
          ...(references ? { references } : {}), // this is a JSON-stringified string !!
          ...(channel ? { channel } : {}),
        }),
      },
    );
  }
}

export default Client4p;
